import { ComponentType, FC } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { gql } from '@apollo/client';
import { graphql } from '@apollo/client/react/hoc';
import cookie from 'js-cookie';
import { APP_LANGUAGES } from '../../../config/common.config';
import { convertToUrlLocale, getIntlValidLocale } from '../../helpers/locale';
import RouteProps from '../RoutesWithLayout/RouteProps';
import canUseDOM from '../../helpers/canUseDOM';
import LanguageSuggestPopupBase, {
  LanguageSuggestPopupProps,
} from './LanguageSuggestPopup';

interface LanguageSuggestProps extends RouteProps, LanguageSuggestPopupProps {
  showPopup: boolean;
}

interface QueryResult {
  loading: boolean;
  user: {
    language: string;
    auth: {
      state: string;
    };
  };
}

interface PreferredLanguageQueryOutputProps {
  showPopup: boolean;
  preferredLanguage?: string;
  onClose?: () => void;
}

export const AUTHENTICATED_QUERY = gql`
  query GetUserLanguage {
    user {
      id
      language
      auth {
        state
      }
    }
  }
`;

const AUTHENTICATED = 'AUTHENTICATED';
const LANGUAGE_PREFERENCE_COOKIE = 'lang_pref';

export const LanguageSuggest = ({
  showPopup,
  preferredLanguage,
  ...props
}: LanguageSuggestProps): JSX.Element | null => {
  return showPopup ? (
    <LanguageSuggestPopupBase
      preferredLanguage={preferredLanguage}
      {...props}
    />
  ) : null;
};

const LanguageSuggestPopupContainer = compose(
  withRouter,
  graphql<
    RouteProps,
    QueryResult,
    {},
    PreferredLanguageQueryOutputProps | void
  >(AUTHENTICATED_QUERY, {
    props: ({ data, ownProps }) => {
      if (!data || data.loading) {
        return { showPopup: false };
      }
      const currentLanguage = getIntlValidLocale(
        ownProps.match.params.language
      );
      const queryResult = data as QueryResult;
      const { user } = queryResult;
      const isAuthenticated = user?.auth?.state === AUTHENTICATED;
      const preferredLanguage = isAuthenticated
        ? user.language
        : convertToUrlLocale(window.navigator.language.split('-')[0]);

      if (!APP_LANGUAGES.includes(preferredLanguage)) {
        return { showPopup: false };
      }

      if (currentLanguage === preferredLanguage) {
        return { showPopup: false };
      }

      return {
        showPopup: true,
        preferredLanguage,
        onClose: () =>
          cookie.set(LANGUAGE_PREFERENCE_COOKIE, String(+new Date())),
      };
    },
  })
)(LanguageSuggest) as ComponentType;

const LanguageSuggestPopup: FC = () =>
  canUseDOM() && !cookie.get(LANGUAGE_PREFERENCE_COOKIE) ? (
    <LanguageSuggestPopupContainer />
  ) : null;

export default LanguageSuggestPopup;
